import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../SharedControlPanel.scss';
import './GlossariesPanel.scss';
import useProject from '../../../../../hooks/useProject';
import TickIcon from '../../../../../images/tick-destination-icon.png';
import { DefaultGlossary, Glossary } from '../../../../../types/Glossary';
import { GlossaryModal } from '../../../../Modals/GlossaryModal/GlossaryModal';

type GlossariesPanelProps = {
  onUpdate: () => void;
};

const GlossariesPanel = ({ onUpdate }: GlossariesPanelProps) => {
  const { t } = useTranslation();
  const { project } = useProject();

  const glossaries = project?.stream?.glossaries ?? [];
  const maxNumberOfGlossaries = 10;
  const defaultGlossary = DefaultGlossary;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [selectedGlossary, setSelectedGlossary] = useState<Glossary>({
    ...defaultGlossary,
    languageIsoCode: project?.stream?.sourceLanguageIsoCode || 'en-us',
  });

  const openGlossaryModel = (
    userId = '',
    modelId = '',
    id = '',
    languageIsoCode = project?.stream?.sourceLanguageIsoCode || 'en-us',
    name = '',
    replace = '[{"phrase": ""}]',
    format = '[{"phrase": ""}]',
    doNotTranslate = '[{"phrase": ""}]',
    tokens = '[{"find": "", "replace": ""}]',
    profanity = false
  ) => {
    setSelectedGlossary({
      ...defaultGlossary,
      id: id,
      userId: userId,
      modelId: modelId,    
      languageIsoCode: languageIsoCode,
      name: name,
      replace: replace,
      format: format,
      doNotTranslate: doNotTranslate,
      tokens: tokens,
      profanity: profanity
    });
    setIsModalOpen(true);
  };

  const createAddGlossaryButtons = () => {
    const addGlossaryButtons = [];

    for (let index = 0; index < maxNumberOfGlossaries - (glossaries?.length ?? 0); index++) {
      
      addGlossaryButtons.push(
        <button
          key={index}
          className="control-panel-section-box control-panel-section-box-new"
          onClick={() => openGlossaryModel()}
        >
          <span className="control-panel-section-box-text">
            + {t('producer.control-panel.glossaries.add-new')}
          </span>
        </button>,
      );
    }

    if ((glossaries?.length ?? 0) >= maxNumberOfGlossaries) {
      addGlossaryButtons.push(
        <button
          key={glossaries?.length ?? 0}
          className="control-panel-section-box control-panel-section-box-new"
          onClick={() => openGlossaryModel()}
        >
          <span className="control-panel-section-box-text">
            + {t('producer.control-panel.glossaries.add-new')}
          </span>
        </button>,
      );
    }

    return addGlossaryButtons;
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    onUpdate();
  };

  return (
    <div className="stream-source-panel">
      <div className="stream-source-panel-section">
        <div className="stream-source-panel-section-content">
          {glossaries &&
            glossaries.length > 0 &&
            glossaries.map((glossary: Glossary, index: number) => (
              <button
                key={index}
                className="control-panel-section-box"
                onClick={() =>
                  openGlossaryModel(
                    glossary.userId,
                    glossary.modelId,
                    glossary.id,
                    glossary.languageIsoCode,
                    glossary.name,
                    glossary.replace,
                    glossary.format,
                    glossary.doNotTranslate,
                    glossary.tokens,
                    glossary.profanity
                  )}
              >
                <span className="control-panel-section-box-text">{glossary?.name}</span>
                <img src={TickIcon} alt="tick" className="control-panel-section-box-tick" />
              </button>
            ))}
          {createAddGlossaryButtons()}
        </div>
      </div>

      {isModalOpen && (
        <GlossaryModal
          isOpen={isModalOpen}
          contentLabel="Glossary Modal"
          selectedGlossary={selectedGlossary}
          handleCloseModal={handleCloseModal}
        />
      )}
    </div>
  );
};

export default React.memo(GlossariesPanel);
